import React, { useEffect, useState, useRef } from 'react'
import { useAuth } from '../../context/useAuth'
import { useNls } from '../../nls/nlsProvider'
import { Login } from '../Auth/Login'
import { FamilyStatePage } from './FamilyStatePage'
import { Button } from '../Common'
import { joinEduPtmMeeting, mapEduPtmError } from '../../services/eduPtmService'

export const EduPtmJoinRoute: React.FC = () => {
  const { t } = useNls()
  const { currentUser, isAuthLoading, isSessionLocked } = useAuth()
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const workspaceId = params?.get('workspaceId') || ''
  const bookingId = params?.get('bookingId') || ''

  const [error, setError] = useState<string | null>(() =>
    !workspaceId || !bookingId ? t('NLSEduPtmErrorGeneric') : null,
  )
  const [isJoining, setIsJoining] = useState<boolean>(() => Boolean(workspaceId && bookingId))
  const hasAttemptedRef = useRef(false)

  useEffect(() => {
    if (isAuthLoading || !currentUser || isSessionLocked) return
    if (!workspaceId || !bookingId) return

    if (hasAttemptedRef.current) return
    hasAttemptedRef.current = true

    let active = true
    void Promise.resolve().then(() => {
      if (active) {
        setIsJoining(true)
        setError(null)
      }
    })

    joinEduPtmMeeting({ workspaceId, bookingId })
      .then((res) => {
        if (!active) return
        if (res?.joinUrl) {
          if (typeof window !== 'undefined') {
            window.location.href = res.joinUrl
          }
        } else {
          setIsJoining(false)
          setError(t('NLSEduPtmErrorGeneric'))
        }
      })
      .catch((err) => {
        if (!active) return
        setIsJoining(false)
        const errorKey = mapEduPtmError(err)
        setError(t(errorKey))
      })

    return () => {
      active = false
    }
  }, [currentUser, isAuthLoading, isSessionLocked, workspaceId, bookingId, t])

  if (isAuthLoading) {
    return (
      <FamilyStatePage
        body={t('NLSFamilyLoading')}
        ariaLabel={t('NLSFamilyAriaLoading')}
        testId="ptm-join-auth-loading"
      />
    )
  }

  if (!currentUser) return <Login />
  if (isSessionLocked) return <Login isLocked />

  if (error) {
    return (
      <FamilyStatePage
        tone="danger"
        title={t('NLSFamilyPtmJoinErrorTitle')}
        body={error}
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href = '/family'
              }
            }}
            data-testid="ptm-join-back-family-btn"
          >
            {t('NLSFamilyBackToFamily')}
          </Button>
        }
        testId="ptm-join-error"
      />
    )
  }

  if (isJoining || !error) {
    return (
      <FamilyStatePage
        body={t('NLSFamilyPtmJoiningMeeting')}
        ariaLabel={t('NLSFamilyPtmJoiningMeeting')}
        testId="ptm-join-connecting"
      />
    )
  }

  return null
}

export default EduPtmJoinRoute
